// ============================================================
// src/components/Dashboard.jsx
// Pantalla principal: muestra materias, progreso y stats
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get, set } from 'firebase/database';
import { database } from '../services/firebase';

// Importar todos los JSON de contenido
import fisicaData from '../data/fisica_openmind.json';
import quimicaData from '../data/quimica_openmind.json';
import biologiaData from '../data/biologia_openmind.json';
import historiaData from '../data/historia_openmind.json';
import politicaData from '../data/politica_openmind.json';
import arteData from '../data/arte_openmind.json';

// Catálogo de materias con colores y metadatos visuales
const SUBJECTS = [
  {
    ...fisicaData,
    color: 'from-blue-500 to-cyan-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    accent: 'text-blue-600',
    lightBg: 'bg-blue-100',
  },
  {
    ...quimicaData,
    color: 'from-emerald-500 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    accent: 'text-emerald-600',
    lightBg: 'bg-emerald-100',
  },
  {
    ...biologiaData,
    color: 'from-purple-500 to-violet-500',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    accent: 'text-purple-600',
    lightBg: 'bg-purple-100',
  },
  {
    ...historiaData,
    color: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    accent: 'text-amber-600',
    lightBg: 'bg-amber-100',
  },
  {
    ...politicaData,
    color: 'from-red-500 to-rose-500',
    bg: 'bg-red-50',
    border: 'border-red-200',
    accent: 'text-red-600',
    lightBg: 'bg-red-100',
  },
  {
    ...arteData,
    color: 'from-pink-500 to-fuchsia-500',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    accent: 'text-pink-600',
    lightBg: 'bg-pink-100',
  },
];

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [stats, setStats] = useState({
    totalPoints: 0,
    level: 1,
    streak: 0,
    completedTopics: 0,
  });
  const [loading, setLoading] = useState(true);

  // Cargar progreso del usuario desde Firebase
  useEffect(() => {
    if (!user) return;
    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    try {
      const progressRef = ref(database, `users/${user.uid}/progress`);
      const statsRef = ref(database, `users/${user.uid}/stats`);

      const [progressSnap, statsSnap] = await Promise.all([
        get(progressRef),
        get(statsRef),
      ]);

      // Si no tiene progreso, inicializarlo
      if (!progressSnap.exists()) {
        const emptyProgress = buildEmptyProgress();
        await set(progressRef, emptyProgress);
        setProgress(emptyProgress);
      } else {
        setProgress(progressSnap.val());
      }

      // Si no tiene stats, inicializarlos
      if (!statsSnap.exists()) {
        const initialStats = {
          totalPoints: 0,
          level: 1,
          streak: 0,
          completedTopics: 0,
          lastActive: new Date().toISOString(),
        };
        await set(statsRef, initialStats);
        setStats(initialStats);
      } else {
        setStats(statsSnap.val());
      }
    } catch (err) {
      console.error('Error cargando datos del usuario:', err);
    } finally {
      setLoading(false);
    }
  };

  // Construir estructura vacía de progreso para un nuevo usuario
  const buildEmptyProgress = () => {
    const result = {};
    SUBJECTS.forEach((subject) => {
      result[subject.subject] = {};
      subject.levels.forEach((level) => {
        level.topics.forEach((topic) => {
          result[subject.subject][topic.topicId] = {
            completed: false,
            score: 0,
            practiceAnswers: {},
            challengeSubmitted: false,
          };
        });
      });
    });
    return result;
  };

  // Calcular progreso de una materia
  const getSubjectProgress = (subject) => {
    const subjectProg = progress[subject.subject] || {};
    const allTopics = subject.levels.flatMap((l) => l.topics);
    const completed = allTopics.filter(
      (t) => subjectProg[t.topicId]?.completed
    ).length;
    return {
      completed,
      total: allTopics.length,
      percent: Math.round((completed / allTopics.length) * 100),
    };
  };

  // Total de temas completados en todas las materias
  const totalCompleted = SUBJECTS.reduce((acc, s) => {
    return acc + getSubjectProgress(s).completed;
  }, 0);

  // Nombre corto del usuario
  const firstName = user?.displayName
    ? user.displayName.split(' ')[0]
    : user?.email?.split('@')[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl animate-bounce mb-3"><img src="logo.png" alt="" className="w-20 h-20 mx-auto object-contain"/></div>
          <p className="text-gray-500">Cargando tu progreso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero de bienvenida ─────────────────────────────── */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-4 pt-8 pb-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            {/* Bienvenida */}
            <div className="text-white">
              <p className="text-indigo-200 text-sm font-medium mb-1">
                ¡Bienvenido de vuelta!
              </p>
              <h1 className="text-3xl md:text-4xl font-extrabold">
                Hola, {firstName} 👋
              </h1>
              <p className="text-indigo-200 mt-1">
                {totalCompleted} de 120 temas completados
              </p>
            </div>

            {/* Stats rápidas */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: '⭐', value: stats.totalPoints, label: 'Puntos' },
                { icon: '🎯', value: `Nv. ${stats.level}`, label: 'Nivel' },
                { icon: '🔥', value: stats.streak || 0, label: 'Racha' },
              ].map(({ icon, value, label }) => (
                <div
                  key={label}
                  className="bg-white/20 backdrop-blur rounded-2xl px-4 py-3 text-center"
                >
                  <div className="text-2xl">{icon}</div>
                  <div className="text-white font-extrabold text-lg leading-tight">
                    {value}
                  </div>
                  <div className="text-indigo-200 text-xs">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Barra de progreso global */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-indigo-200 mb-1">
              <span>Progreso global</span>
              <span>{Math.round((totalCompleted / 120) * 100)}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-3">
              <div
                className="bg-white h-3 rounded-full transition-all duration-700"
                style={{ width: `${Math.round((totalCompleted / 120) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Grid de materias ──────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 -mt-6 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SUBJECTS.map((subject) => {
            const prog = getSubjectProgress(subject);
            return (
              <button
                key={subject.subject}
                onClick={() => navigate(`/subject/${subject.subject}`)}
                className={`${subject.bg} ${subject.border} border-2 rounded-3xl p-5 text-left
                  hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group`}
              >
                {/* Icono y nombre */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${subject.color}
                        flex items-center justify-center text-2xl shadow-md`}
                    >
                      {subject.icon}
                    </div>
                    <div>
                      <h3 className="font-extrabold text-gray-800">
                        {subject.displayName}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {prog.completed}/{prog.total} temas
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-lg font-bold ${subject.accent} opacity-0
                      group-hover:opacity-100 transition-opacity`}
                  >
                    →
                  </span>
                </div>

                {/* Descripción corta */}
                <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                  {subject.description}
                </p>

                {/* Barra de progreso */}
                <div>
                  <div className="w-full bg-white/60 rounded-full h-2.5 mb-1">
                    <div
                      className={`h-2.5 rounded-full bg-gradient-to-r ${subject.color} transition-all duration-700`}
                      style={{ width: `${prog.percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className={`${subject.accent} font-semibold`}>
                      {prog.percent}% completado
                    </span>
                    <span className="text-gray-400">
                      {prog.total - prog.completed} restantes
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Cómo funciona ────────────────────────────────── */}
        <div className="mt-10">
          <h2 className="text-xl font-bold text-gray-800 mb-4">¿Cómo funciona?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: '📖',
                title: 'Lee y comprende',
                desc: 'Explicaciones claras que estimulan el pensamiento crítico, no la memorización.',
              },
              {
                icon: '✏️',
                title: 'Practica con sentido',
                desc: 'Preguntas abiertas y de opción múltiple con retroalimentación constructiva.',
              },
              {
                icon: '🚀',
                title: 'Desafíos del mundo real',
                desc: 'Aplica lo aprendido en situaciones reales y gana puntos y badges.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm"
              >
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-gray-800 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
